/* Core & libraries */
import React, { useState, useRef, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Formik } from "formik";
import { Link, useNavigate, useLocation } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import {
  ADD_SPECIFIC_POLICY_ROUTE,
  EDIT_SPECIFIC_POLICY_ROUTE,
  POLICY_ROUTE,
} from "../../constants/routes.js";
import { usePolicyService } from "services/policyService.js";
import {
  aaPolicyCabinOptions,
  ACCEPTED_LANGUAGES,
  AIRLINES,
} from '../../constants/appConstants.js';
import { useHasPermission } from "../../hooks/useHasPermission.js";
import { useGlobalContext } from "contexts/globalContext.js";
import { useGlobalUpdateContext } from "contexts/globalContext.js";
import { useUserContext } from "contexts/userContext";
import moment from 'helpers/moment';

/* Components */
import UCButton from "../../components/buttons/index.js";
import { ReactComponent as SpecificPolicyImg } from "../../resources/images/specific-policy.svg";
import { ReactComponent as SpecificPolicyArrow } from "../../resources/images/specific-policy-arrow.svg";
import { ReactComponent as SpecificPolicyArrows } from "../../resources/images/specific-policy-arrows.svg";
import PolicySettingsCard from "./policySettingsCard/index.js";
import FlightDurationCard from "./flightDurationCard/index.js";
import Breadcrumbs from "../../components/breadcrumbs/index.js";
import EditPolicyModal from "../../components/modals/editPolicyModal/index.js";
import PolicySkeleton from "./skeleton/policySkeleton.js";
import PolicyUnsavedChangesModal from "../../components/modals/policyUnsavedChangesModal/index.js";

/* Styles */
import styles from "./styles.js";
import { useLocaleContext } from "../../contexts/localeContext.js";

const EditPolicy = () => {
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();

  const globalProperties = useGlobalContext();
  const setGlobalProperties = useGlobalUpdateContext();
  const { state } = useLocation();
  const formRef = useRef(null);
  const [showEditPolicyModal, setShowEditPolicyModal] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const { getPolicyDetails, editPolicyDetails } = usePolicyService();
  const [policyResponse, setPolicyResponse] = useState({});
  const [hasMedium, setHasMedium] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [proceedAfterSubmit, setProceedAfterSubmit] = useState(false);
  const globalLocale = useLocaleContext();
  const userHasEditPermission = checkPermission("F_POLICY_MANAGEPOLICY")

  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const isChoice = providerCode === "CHOICE";

  useEffect(() => {
    (async () => {
      const response = await getPolicyDetails(state?.policyId);
      if (response) {
        setPolicyResponse(response);
        setHasMedium(response.durationRules?.length === 3);
      }
    })();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formRef.current?.dirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  //set policy info so that they are ssaved regardless of what the user selects afterwards
  useEffect(() => {
    setGlobalProperties({
      ...globalProperties,
      addFirstPolicyData: {
        ...globalProperties.addFirstPolicyData,
        policyName: state?.policyName,
        policyId: state?.policyId
      }
    })
  }, [])

  const navigateWithConfirmation = (navigationAction) => {
    if (formRef.current?.dirty) {
      setPendingNavigation(() => navigationAction);
      setShowUnsavedChangesModal(true);
    } else {
      navigationAction();
    }
  };

  const handleSaveAndContinue = () => {
    setProceedAfterSubmit(true);
    formRef.current.handleSubmit();
  };

  const handleCancel = () => {
    setShowUnsavedChangesModal(false);
    setPendingNavigation(null);
  };

  const transformFormValuesToPolicy = (
    formValues,
    hasMediumFlight,
    existingPolicyData
  ) => {
    const transformedData = {
      policyName: existingPolicyData?.policyName,
      enableMediumFlights: hasMediumFlight,
      generalRule: {
        maxTripFare: formValues.maxTripFare ? String(formValues.maxTripFare) : '0',
        currencyCode: formValues.currency || 'USD',
        policyRuleId: existingPolicyData?.generalRule?.policyRuleId || '',
      },
      policySettings: {
        policyRuleId: existingPolicyData?.policySettings?.policyRuleId || '',
        advancePurchase: formValues.advancePurchase,
        requiredAdvancePurchase: formValues.requiredAdvancePurchase,
        allowRefundableFares: formValues.allowRefundableFares,
        allowBasicFares: formValues.allowBasicFares,
        requiredRefundableFares: formValues.requiredRefundableFares,
      },

      durationRules: [
        {
          policyRuleId: existingPolicyData?.durationRules[0]?.policyRuleId || '',
          durations: [
            {
              value: formValues.shortDurationValue,
              operator: 'LT',
            },
          ],
          durationType: 'SHORT',
          classOfService: formValues.shortCabinOptionValue,
        },
        {
          policyRuleId: existingPolicyData?.durationRules[1]?.policyRuleId || '',
          durations: [
            {
              value: formValues.longDurationValue,
              operator: 'GT',
            },
          ],
          durationType: 'LONG',
          classOfService: formValues.longCabinOptionValue,
        },
      ],
    };

    if (hasMediumFlight) {
      transformedData.durationRules.push({
        policyRuleId: existingPolicyData?.durationRules[2]?.policyRuleId || "",
        durations: [
          {
            value: formValues.shortDurationValue,
            operator: "GE",
          },
          {
            value: formValues.longDurationValue,
            operator: "LE",
          },
        ],
        durationType: "MEDIUM",
        classOfService: formValues.mediumCabinOptionValue,
      });
    }

    return transformedData;
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const submitForm = async (values, setSubmitting) => {
    const data = transformFormValuesToPolicy(values, hasMedium, policyResponse);
    const success = await editPolicyDetails(data, state?.policyId, globalProperties.firstTimeSaved);
    setSubmitting(false);

    if (success) {
      if (proceedAfterSubmit && pendingNavigation) {
        pendingNavigation();
        setProceedAfterSubmit(false);
        setPendingNavigation(null);
      } else {
        navigate("../" + POLICY_ROUTE);
      }
    }
  };

  const LastUpdatedMessage = ({ updatedBy, updatedOn }) => {
    const updatedDate = updatedOn ? moment(updatedOn).toDate() : null;
    const formattedDate = updatedDate.toLocaleDateString(ACCEPTED_LANGUAGES[globalLocale], {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
    const formattedTime = updatedDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });

    return (
      <FormattedMessage
        id="editPolicy.lastUpdatedText"
        values={{
          date: formattedDate,
          time: formattedTime,
          name: updatedBy,
        }}
      />
    );
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  return (
    <div>
      <div className={classes.pageHeader}>
        <Breadcrumbs
          items={[
            <Link to={POLICY_ROUTE}>
              <FormattedMessage id="breadcrumbs.policy" />
            </Link>,
            policyResponse?.policyName,
          ]}
        />
        <Container className={classes.container}>
          <Row>
            <Col xs={12} sm={8} className="gx-0">
              <h1 className={classes.title}>{policyResponse?.policyName}</h1>
              <h3 className={classes.subtitle}>
                <FormattedMessage
                  id={
                    providerCode === AIRLINES.AS ? 'editPolicy.subtitle.AS' : 'editPolicy.subtitle'
                  }
                />
              </h3>
            </Col>
            {userHasEditPermission && (
              <Col xs={12} sm={4} className={classes.btnWrapper}>
                <UCButton
                  type="default"
                  className={classes.policyBtn}
                  onClick={() => setShowEditPolicyModal(true)}
                  text={<FormattedMessage id="editPolicy.editNameBtn" />}
                />
              </Col>
            )}
          </Row>
        </Container>
      </div>
      <hr className={classes.divider} />
      {Object.keys(policyResponse).length > 0 ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            maxTripFare: policyResponse?.generalRule?.maxTripFare || '0',
            currency: policyResponse?.generalRule?.currencyCode || 'USD',
            allowBasicFares: policyResponse?.policySettings?.allowBasicFares,
            requiredAdvancePurchase: policyResponse?.policySettings?.requiredAdvancePurchase,
            advancePurchase: policyResponse?.policySettings?.advancePurchase,
            allowRefundableFares: policyResponse?.policySettings?.allowRefundableFares,
            requiredRefundableFares: policyResponse?.policySettings?.requiredRefundableFares,
            shortDurationValue: policyResponse?.durationRules[0]?.durations[0]?.value,
            longDurationValue: policyResponse?.durationRules[1]?.durations[0]?.value,
            shortCabinOptionValue:
              policyResponse?.durationRules[0]?.classOfService ||
              aaPolicyCabinOptions[aaPolicyCabinOptions.length - 1].value,
            longCabinOptionValue:
              policyResponse?.durationRules[1]?.classOfService ||
              aaPolicyCabinOptions[aaPolicyCabinOptions.length - 1].value,
            mediumCabinOptionValue:
              policyResponse?.durationRules[2]?.classOfService ||
              aaPolicyCabinOptions[aaPolicyCabinOptions.length - 1].value,
          }}
          onSubmit={(values, { setSubmitting }) => {
            submitForm(values, setSubmitting);
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
          }) => (
            <>
              <form onSubmit={handleSubmit}>
                <PolicySettingsCard
                  values={values}
                  handleFormSubmit={handleFormSubmit}
                  setFieldValue={setFieldValue}
                  formRef={formRef}
                  showSaveBtn={state?.showSaveBtn}
                  userHasEditPermission={userHasEditPermission}
                />
                <FlightDurationCard
                  values={values}
                  hasMediumFlight={hasMedium}
                  setFieldValue={setFieldValue}
                  setHasMedium={setHasMedium}
                  userHasEditPermission={userHasEditPermission}
                />
                <div className={classes.policyContainer}>
                  <div className={classes.specificPolicyHeader}>
                    <div>
                      <h3>
                        <FormattedMessage
                          id={
                            providerCode === AIRLINES.AS
                              ? 'editPolicy.specificTitle.AS'
                              : 'editPolicy.specificTitle'
                          }
                        />
                      </h3>
                      <p>
                        <FormattedMessage
                          id={
                            providerCode === AIRLINES.AS
                              ? 'editPolicy.specificSubtitle.AS'
                              : 'editPolicy.specificSubtitle'
                          }
                        />
                      </p>
                    </div>
                    {userHasEditPermission && (
                      <UCButton
                        type="primary"
                        className={classes.policyBtn}
                        onClick={() =>
                          navigateWithConfirmation(() => {
                            navigate(
                              '../' +
                                ADD_SPECIFIC_POLICY_ROUTE.replace(':policyId', state?.policyId),
                              {
                                state: {
                                  policyName: policyResponse?.policyName,
                                  policyId: state?.policyId,
                                  showSaveBtn: state?.showSaveBtn,
                                },
                              },
                            );
                          })
                        }
                        text={
                          <FormattedMessage
                            id={
                              providerCode === AIRLINES.AS
                                ? 'editPolicy.specificCreateBtn.AS'
                                : 'editPolicy.specificCreateBtn'
                            }
                          />
                        }
                      />
                    )}
                  </div>
                  {!policyResponse?.flightPolicies ||
                  policyResponse?.flightPolicies?.length === 0 ? (
                    <div className={classes.policyCard}>
                      <div className={classes.specificPolicyBox}>
                        <SpecificPolicyImg />
                        <h6>
                          <FormattedMessage
                            id={
                              providerCode === AIRLINES.AS
                                ? 'editPolicy.specificEmptyTitle.AS'
                                : 'editPolicy.specificEmptyTitle'
                            }
                          />
                        </h6>
                        <p>
                          <FormattedMessage
                            id={
                              providerCode === AIRLINES.AS
                                ? 'editPolicy.specificEmptySubitle.AS'
                                : 'editPolicy.specificEmptySubitle'
                            }
                          />
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={classes.specificPolicyWrapper}>
                      {policyResponse?.flightPolicies?.map((item, i) => (
                        <div key={i} className={classes.policyCard}>
                          <div className={classes.specificPolicyContainer}>
                            <div className={classes.specificPolicyInfo}>
                              <div>
                                <h6>
                                  {item.fromLocationName}{' '}
                                  {item.from &&
                                    (item.fromLocationType === 'CITY' ||
                                      item.fromLocationType === 'AIRPORT') &&
                                    `(${item.from})`}
                                </h6>
                                <span>
                                  {item.fromLocationType && (
                                    <FormattedMessage
                                      id={`specificPolicy.locationType.${capitalizeFirstLetter(item.fromLocationType)}`}
                                    />
                                  )}
                                </span>
                                {item.biDirectional ? (
                                  <SpecificPolicyArrows />
                                ) : (
                                  <SpecificPolicyArrow />
                                )}
                                <h6>
                                  {item.toLocationName}{' '}
                                  {item.to &&
                                    (item.toLocationType === 'CITY' ||
                                      item.toLocationType === 'AIRPORT') &&
                                    `(${item.to})`}
                                </h6>
                                <span>
                                  {item.toLocationType && (
                                    <FormattedMessage
                                      id={`specificPolicy.locationType.${capitalizeFirstLetter(item.toLocationType)}`}
                                    />
                                  )}
                                </span>
                              </div>
                              <span>{aaPolicyCabinOptions[+item.classOfService - 1].label}</span>
                            </div>
                            {userHasEditPermission && (
                              <UCButton
                                type="general"
                                className={classes.editSpecificBtn}
                                onClick={() =>
                                  navigateWithConfirmation(() => {
                                    navigate(
                                      '../' +
                                        EDIT_SPECIFIC_POLICY_ROUTE.replace(
                                          ':policyId',
                                          state?.policyId,
                                        ),
                                      {
                                        state: {
                                          policyName: policyResponse?.policyName,
                                          policyId: state?.policyId,
                                          data: item,
                                          showSaveBtn: state?.showSaveBtn,
                                        },
                                      },
                                    );
                                  })
                                }
                                text={<FormattedMessage id="editPolicy.specificEditBtn" />}
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={classes.policyFooter}>
                  {policyResponse?.updatedOn && (
                    <LastUpdatedMessage
                      updatedBy={policyResponse?.updatedBy}
                      updatedOn={policyResponse?.updatedOn}
                    />
                  )}
                </div>
              </form>
            </>
          )}
        </Formik>
      ) : (
        <PolicySkeleton />
      )}
      <EditPolicyModal
        showModal={showEditPolicyModal}
        setShowModal={setShowEditPolicyModal}
        policyName={policyResponse?.policyName}
        policyId={state?.policyId}
        setPolicyResponse={setPolicyResponse}
      />
      <PolicyUnsavedChangesModal
        showModal={showUnsavedChangesModal}
        setShowModal={setShowUnsavedChangesModal}
        onSave={handleSaveAndContinue}
        onCancel={handleCancel}
      />
    </div>
  );
};

export default EditPolicy;
